import { IconDotsVertical, IconTrash } from "@tabler/icons-react";
import { type ReactNode } from "react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "#app/components/ui/dropdown-menu";
import { cn } from "#app/utils/misc.tsx";

type ChatListItemProps = {
  title: string;
  isActive?: boolean;
  isDeleting?: boolean;
  leadingIcon?: ReactNode;
  /** Trailing status marker (icon or group of icons). Kept clear of the menu. */
  badge?: ReactNode;
  onSelect: () => void;
  onDelete: () => void;
  deleteLabel: string;
  moreLabel: string;
};

export function ChatListItem({
  title,
  isActive,
  isDeleting,
  leadingIcon,
  badge,
  onSelect,
  onDelete,
  deleteLabel,
  moreLabel,
}: ChatListItemProps) {
  return (
    <div
      className={cn(
        "group relative m-0 flex items-center rounded-full transition-colors",
        "hover:bg-zinc-100 dark:hover:bg-zinc-800",
        isActive &&
          "bg-zinc-200/60 font-medium text-black dark:bg-zinc-800 dark:text-white",
        isDeleting && "pointer-events-none opacity-50",
      )}
    >
      <button
        type="button"
        onClick={onSelect}
        title={title}
        aria-current={isActive ? "true" : undefined}
        className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 truncate px-3 py-1.5 text-left text-sm text-foreground group-hover:pr-8"
      >
        {leadingIcon ? (
          <span className="inline-flex shrink-0 text-muted-foreground">
            {leadingIcon}
          </span>
        ) : null}
        <span className="min-w-0 flex-1 truncate">{title}</span>
        {badge ? <span className="shrink-0">{badge}</span> : null}
      </button>

      {/* Overlay the menu so the active pill hugs the title (not a reserved
          trailing column that makes the highlight look boxy). */}
      <div className="opacity-100 transition-opacity duration-200 sm:absolute sm:top-1/2 sm:right-0.5 sm:-translate-y-1/2 sm:opacity-0 sm:group-hover:opacity-100 sm:focus-within:opacity-100">
        <DropdownMenu>
          <DropdownMenuTrigger
            aria-label={moreLabel}
            className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-full hover:bg-zinc-200 dark:hover:bg-zinc-700"
          >
            <IconDotsVertical size={16} />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem variant="destructive" onClick={onDelete}>
              <IconTrash size={16} />
              <span>{deleteLabel}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

export default ChatListItem;
